<template>
  <div>
    <h3 class="heading-3 mb-16">
      {{ i18n(translations.title) }}
    </h3>

    <RevCard>
      <div ref="errorMessageBlock" class="flex">
        <RevInfoBlock
          v-if="errorMessage"
          class="mx-20 mb-20 mt-24"
          data-test="payment-error"
          dismissable
          :icon="IconHeartExploded"
          :title="errorMessage?.title || ''"
          variant="warning"
        >
          <div class="body-2 mt-8">
            <p>{{ errorMessage?.description }}</p>
          </div>
        </RevInfoBlock>
      </div>

      <ErrorBoundary :owners="['bot-squad-payin-front']">
        <PaymentForm
          ref="paymentForm"
          :base-price="priceAfterDiscount"
          :create-payment
          :pass-through-on-zero-price="true"
          :payment-methods="paymentMethods ?? []"
          :resume-payment-id
          @setup-error="handleSetupError"
          @submit-error="handleSubmitError"
          @submit-start="handleSubmitStart"
          @submit-success="handleSubmitSuccess"
          @update:selected-method="handleMethodChange"
        />
        <CheckoutSubmitButton :form="paymentForm" />
      </ErrorBoundary>
    </RevCard>

    <GDPRNotice page="payment" />
    <template v-if="!selectedMobilePlan">
      <FlashSalesDiscountCard
        v-if="hasFlashSalesDiscount"
        class="mt-56"
        :items
        @removed="emit('update')"
      />
      <DiscountCard v-else class="mt-56" />
    </template>
    <ReassuranceItems class="mt-56" :selected-mobile-plan />
  </div>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { computed, onMounted, ref } from 'vue'

import type { BuybackOffer } from '@backmarket/http-api'
import { postOrder } from '@backmarket/http-api/src/api-specs-buyback/customer/customer'
import { postCreate } from '@backmarket/http-api/src/api-specs-payment/payment/payment'
import type { PaymentMethod } from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import type { HttpApiError } from '@backmarket/http-api/src/utils/HttpApiError'
import { useBuybackOffer } from '@backmarket/nuxt-layer-buyback/composables/buybackOffer/useBuybackOffer'
import type { CartSummary } from '@backmarket/nuxt-layer-checkout/api/cart/types'
import DiscountCard from '@backmarket/nuxt-layer-checkout/components/Discount/DiscountCard.vue'
import FlashSalesDiscountCard from '@backmarket/nuxt-layer-checkout/components/Discount/FlashSalesDiscountCard.vue'
import GDPRNotice from '@backmarket/nuxt-layer-checkout/components/GDPRNotice/GDPRNotice.vue'
import ReassuranceItems from '@backmarket/nuxt-layer-checkout/components/ReassuranceItems/ReassuranceItems.vue'
import {
  type PhonelineSetupInformation,
  useBouyguesStore,
} from '@backmarket/nuxt-layer-checkout/stores/bouyguesStore'
import { useDiscountStore } from '@backmarket/nuxt-layer-checkout/stores/discountStore'
import { getAvailableItems } from '@backmarket/nuxt-layer-checkout/utils/cart/getAvailableItems'
import { getPriceAfterDiscount } from '@backmarket/nuxt-layer-checkout/utils/cart/getPriceAfterDiscount'
import { getFlashSalesDiscount } from '@backmarket/nuxt-layer-checkout/utils/flash-sales'
import { getSelectedMobilePlan } from '@backmarket/nuxt-layer-checkout/utils/mobilePlan/getSelectedMobilePlan'
import {
  CheckoutSubmitButton,
  PaymentForm,
  type PaymentFormCreateFunctionOptions,
  type PaymentFormInstance,
  type PaymentReadableMessage,
  type SetupErrorEvent,
  type SubmitErrorEvent,
  type SubmitStartEvent,
  type SubmitSuccessData,
  toCheckoutPaymentCreateBody,
  usePaymentRedirection,
} from '@backmarket/nuxt-layer-payment/form'
import {
  type PaymentMethodsFeatures,
  useMarketPaymentMethodsFor,
} from '@backmarket/nuxt-layer-payment/methods'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import ErrorBoundary from '@backmarket/nuxt-module-logger/components/ErrorBoundary.vue'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevCard } from '@ds/components/Card'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { IconHeartExploded } from '@ds/icons/IconHeartExploded'

import usePaymentErrors from '../../composables/usePaymentErrors'
import type { Discount } from '../../types/discount'

import translations from './Payment.translations'
import { handleMobilePlanErrors } from './utils/handleMobilePlanErrors'
import { hasInsuranceOffersSelected } from './utils/hasInsuranceOffersSelected'
import { hasMonthlyInsuranceOfferSelected } from './utils/hasMonthlyInsuranceOfferSelected'

const props = defineProps<{
  mobilePlanCustomerInformation?: PhonelineSetupInformation & {
    accessToken: string
  }
  items: ApiSpecs['/bm/cart']['GET']['response']['cartItems']
  summary: CartSummary
  discount: Discount
  swapOffer: BuybackOffer | null
  resultPageName: string
  methodsFeatures?: PaymentMethodsFeatures
  failureUrl?: string
  cancelUrl?: string
}>()

const emit = defineEmits<{
  (e: 'update'): void
  (e: 'loading', message: string): void
  (e: 'loaded'): void
}>()

const experiments = useExperiments()
const i18n = useI18n()
const discountStore = useDiscountStore()
const route = useRoute()
const proceedRedirection = usePaymentRedirection({
  paymentResultLocation: (paymentId) => ({
    type: 'internal',
    name: props.resultPageName,
    params: { paymentId },
  }),
})
const tracking = useTracking()
const bouyguesStore = useBouyguesStore()
const $httpFetchV2 = useHttpFetch()

const { resetBuybackOffer } = useBuybackOffer()
const { handlePaymentErrors } = usePaymentErrors()

const paymentForm = ref<PaymentFormInstance | null>(null)
const errorMessage = ref<PaymentReadableMessage | null>(null)
const errorMessageBlock = ref()

const flashSalesDiscount = computed(() => getFlashSalesDiscount(props.items))
const selectedMobilePlan = computed(() => getSelectedMobilePlan(props.items))
const priceAfterDiscount = computed(() =>
  getPriceAfterDiscount(props.discount, props.summary),
)

const { data: paymentMethods } = await useMarketPaymentMethodsFor({
  listings: getAvailableItems(props.items).map((item) => item.listingId),
  bagPrice: () => priceAfterDiscount.value,
  hasInsurance: hasInsuranceOffersSelected(props.items),
  tokenization: hasMonthlyInsuranceOfferSelected(props.items),
  features: props.methodsFeatures,
})

const resumePaymentId = computed(() => route.query.paymentId as string)

const hasFlashSalesDiscount = computed(
  () =>
    experiments['experiment.flashSales'] === 'enabled' &&
    !!flashSalesDiscount.value,
)

onMounted(() => {
  // Disable back/forward cache: https://web.dev/articles/bfcache/
  // Without this the user that goes back to the payment page will see an infinite loader
  // TODO: Handle the case where the user goes back to the payment page
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      window.location.reload()
    }
  })
})

const createPayment = async (options: PaymentFormCreateFunctionOptions) => {
  try {
    const paymentResponse = await $httpFetch(postCreate, {
      body: {
        failure_url: props.failureUrl,
        cancel_url: props.cancelUrl,
        ...toCheckoutPaymentCreateBody(options),
        customerInformation: props.mobilePlanCustomerInformation
          ? { ...props.mobilePlanCustomerInformation }
          : {},
        ...(isEmpty(discountStore.paymentData)
          ? {}
          : {
              promotion_code_pk: discountStore.paymentData.id,
              amount_promotion: parseFloat(
                discountStore.paymentData.totalDeduction.amount,
              ),
              slider:
                parseFloat(discountStore.paymentData.totalDeduction.amount) -
                parseFloat(discountStore.paymentData.deduction.amount),
              order_slider: 'GO',
            }),
        ...(experiments['experiment.serviceFeeGrid'] === 'newServiceFeeGrid'
          ? { newPriceGrid: true }
          : {}),
      },
    })

    if (props.swapOffer) {
      try {
        await $httpFetch(postOrder, {
          body: {
            payment_id: paymentResponse.paymentId,
            listing_id: props.swapOffer.sourcingListingPublicId,
            shippingId: props.swapOffer.defaultShippingModeId,
          },
        })
      } catch {
        // Remove the swap offer and proceed with the payment -> CK-5631
        await $httpFetchV2('/bm/cart/swap', { method: 'DELETE' })
      } finally {
        resetBuybackOffer()
      }
    }

    return paymentResponse
  } catch (error) {
    handleMobilePlanErrors(error as Error)
    handlePaymentErrors(error as HttpApiError)

    throw error
  }
}

const handleMethodChange = async (
  method: PaymentMethod | null,
  isSelectedByUser: boolean,
) => {
  if (isSelectedByUser) {
    errorMessage.value = null

    tracking.trackClick({
      name: `4-Payment_list-${method?.bmCode}`,
      zone: 'funnel',
    })
  }
}

const handleSubmitStart = ({
  customLoadingMessage,
  paymentMethod,
}: SubmitStartEvent) => {
  errorMessage.value = null
  emit('loading', customLoadingMessage || i18n(translations.loadingPayment))
  tracking.trackClick({
    value: paymentMethod?.bmCode || '',
    name: '4-Payment_cta-pay',
    zone: 'funnel',
  })
}

const showErrorMessage = async (
  message: PaymentReadableMessage | undefined,
) => {
  if (message) {
    errorMessage.value = message
    errorMessageBlock.value?.scrollIntoView({ behavior: 'smooth' })
  }
}

const handleSetupError = async ({ message }: SetupErrorEvent) => {
  await showErrorMessage(message)
}

const handleSubmitSuccess = ({ redirection }: SubmitSuccessData) => {
  bouyguesStore.clear()
  proceedRedirection(redirection)
}

const handleSubmitError = async ({
  redirection,
  message,
}: SubmitErrorEvent) => {
  if (redirection) {
    proceedRedirection(redirection)

    return
  }

  emit('loaded')
  await showErrorMessage(message)
}
</script>
